import { Injectable } from "@angular/core";
import { ArrayHelper, Tools } from "@comunicare/comunicare-helper";
import {
  IAppError,
  IIdentifier,
  IPractitioner,
  IPractitionerInfo,
  IPractitionerWithStatus,
  ISlimUserInfo,
} from "@comunicare/comunicare-interfaces";
import { Observable } from "rxjs";
import { ApiService } from "./api.service";
import { BaseApiService } from "./baseApi.service";

export interface PostPractitionerBody {
  userInfo: unknown;
  practitioner: IPractitioner;
  practitionerFields?: unknown;
  ignoreNameWarning?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class PractitionerApiService extends BaseApiService {
  constructor(private api: ApiService) {
    super(
      "Practitioners", // Theme !
      ["practitioner", "dashboard/resetPractitionerPassword", "dashboard/sendPractitionerPassword"], // Post route paths
      ["practitioners", "dashboard/userInfo", "dashboard/practitionerFromDirectory", "resetOwnPassword"], // Get route paths
      ["practitioner", "ownPractitionerInfos", "dashboard/practitionerActivation"], // Update route paths
      [] // Delete route paths
    );
  }

  public list(): Observable<IPractitioner[]> {
    return this.api.get(this.readRoutes[0]) as Observable<IPractitioner[]>;
  }

  public update(body: PostPractitionerBody): Observable<IPractitioner> {
    return this.api.put(this.updateRoutes[0], body) as Observable<IPractitioner>;
  }
  public updateOwnInfos(body: PostPractitionerBody): Observable<IPractitioner> {
    return this.api.put(this.updateRoutes[1], body) as Observable<IPractitioner>;
  }

  public create(body: PostPractitionerBody): Observable<IPractitioner> {
    return this.api.post(this.createRoutes[0], body) as Observable<IPractitioner>;
  }

  public listForService(service?: string, services?: string[]): Observable<IPractitionerWithStatus[]> {
    const params: Record<string, string | number | boolean | string[] | IIdentifier> = {};
    if (Tools.isDefined(services) && !ArrayHelper.isEmpty(services)) {
      params.services = services.join();
    }
    if (Tools.isDefined(service)) {
      params.service = service;
    }
    return this.api.get(this.readRoutes[0], params) as Observable<IPractitionerWithStatus[]>;
  }

  public getPractitionerInfo(caremateId: string): Observable<ISlimUserInfo> {
    return this.api.get(this.readRoutes[1], { caremateId: caremateId }) as Observable<ISlimUserInfo>;
  }

  public resetPractitionerPassword(caremateId: string, password: string): Observable<Record<string, never>> {
    return this.api.post(this.createRoutes[1], {
      caremateIdentifier: caremateId,
      newPassword: password,
    }) as Observable<Record<string, never>>;
  }

  public sendPractitionerPassword(mail: string, password: string): Observable<Record<string, never>> {
    return this.api.post(this.createRoutes[2], {
      mail,
      password,
    }) as Observable<Record<string, never>>;
  }

  public getPractitionersFromDirectory(family: string, given: string, country: string, mssante?: string): Observable<IPractitionerInfo[]> {
    return mssante
      ? (this.api.get(this.readRoutes[2], { country, mssante }) as Observable<IPractitionerInfo[]>)
      : (this.api.get(this.readRoutes[2], { country, family, given }) as Observable<IPractitionerInfo[]>);
  }

  public resetOwnPassword(login: string, oldPassword: string, newPassword: string): Observable<IAppError> {
    return this.api.get(this.readRoutes[3], { login: login, oldPassword: oldPassword, newPassword: newPassword }) as Observable<IAppError>;
  }

  public setPractitionerActivation(practitionerId: string, activate: boolean): Observable<void> {
    return this.api.put(this.updateRoutes[2], { practitionerId, activate }) as Observable<void>;
  }
}
